import React from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Container, Typography, styled } from "@mui/material";
import Footer from "../componentsPeopletech/Footer/Footer";

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const baseUrl = 'https://www.peoplefortech.com';

  const PrivacyContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: '#ffffff',
    maxWidth: '900px',
  }));

  const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: '#000000',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  }));

  const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '1.5rem',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    color: '#1c2859',
  }));

  const SectionText = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    lineHeight: 1.8,
    color: '#414141',
    marginBottom: theme.spacing(2),
  }));

  const LastUpdated = styled(Typography)(({ theme }) => ({
    fontSize: '0.9rem',
    color: '#666',
    fontStyle: 'italic',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  }));

  return (
    <>
      <Helmet>
        <title>Política de Privacidad - PeopleForTech</title>
        <meta name="description" content="Política de Privacidad de PeopleForTech" />
        <link rel="canonical" href={`${baseUrl}/privacy-policy`} />
        <html lang={currentLang} />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/privacy-policy`} />
        <meta property="og:title" content="Política de Privacidad - PeopleForTech" />
        <meta property="og:description" content="Política de Privacidad de PeopleForTech" />
      </Helmet>
      
      <PrivacyContainer>
        <Title variant="h1">Política de Privacidad</Title>
        <LastUpdated>Última actualización: {new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}</LastUpdated>

        <SectionTitle variant="h2">1. Introducción</SectionTitle>
        <SectionText>
          PeopleForTech ("nosotros", "nuestro" o "la empresa") se compromete a proteger su privacidad. 
          Esta Política de Privacidad explica cómo recopilamos, usamos, divulgamos y protegemos su 
          información personal cuando visita nuestro sitio web www.peoplefortech.com y utiliza 
          nuestros servicios.
        </SectionText>

        <SectionTitle variant="h2">2. Información que Recopilamos</SectionTitle>
        <SectionText>
          <strong>2.1. Información que usted nos proporciona:</strong> Cuando se comunica con nosotros 
          a través de formularios de contacto, correo electrónico o de otra manera, podemos recopilar 
          información como su nombre, dirección de correo electrónico, número de teléfono y cualquier 
          otra información que elija proporcionar.
        </SectionText>
        <SectionText>
          <strong>2.2. Información recopilada automáticamente:</strong> Cuando visita nuestro sitio web, 
          podemos recopilar automáticamente cierta información sobre su dispositivo, incluyendo su 
          dirección IP, tipo de navegador, sistema operativo, páginas visitadas, tiempo de visita y 
          referencias de sitios web.
        </SectionText>
        <SectionText>
          <strong>2.3. Cookies y tecnologías similares:</strong> Utilizamos cookies y tecnologías similares 
          para mejorar su experiencia, analizar el tráfico del sitio y personalizar el contenido. 
          Puede controlar el uso de cookies a través de la configuración de su navegador.
        </SectionText>

        <SectionTitle variant="h2">3. Cómo Utilizamos su Información</SectionTitle>
        <SectionText>
          Utilizamos la información recopilada para:
        </SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li>Responder a sus consultas y proporcionar servicios de consultoría</li>
          <li>Mejorar nuestro sitio web y servicios</li>
          <li>Enviar comunicaciones relacionadas con nuestros servicios (con su consentimiento)</li>
          <li>Cumplir con obligaciones legales y proteger nuestros derechos</li>
          <li>Analizar el uso del sitio web y realizar mejoras</li>
        </SectionText>

        <SectionTitle variant="h2">4. Compartir su Información</SectionTitle>
        <SectionText>
          No vendemos, alquilamos ni compartimos su información personal con terceros, excepto en 
          las siguientes circunstancias:
        </SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li><strong>Proveedores de servicios:</strong> Podemos compartir información con proveedores 
          de servicios que nos ayudan a operar nuestro sitio web y proporcionar nuestros servicios 
          (por ejemplo, servicios de hosting, análisis web).</li>
          <li><strong>Requisitos legales:</strong> Podemos divulgar información si es requerido por ley 
          o para proteger nuestros derechos legales.</li>
          <li><strong>Con su consentimiento:</strong> Podemos compartir información con su consentimiento 
          explícito.</li>
        </SectionText>

        <SectionTitle variant="h2">5. Seguridad de la Información</SectionTitle>
        <SectionText>
          Implementamos medidas de seguridad técnicas y organizativas apropiadas para proteger su 
          información personal contra acceso no autorizado, alteración, divulgación o destrucción. 
          Sin embargo, ningún método de transmisión por Internet o almacenamiento electrónico es 100% 
          seguro, y no podemos garantizar la seguridad absoluta.
        </SectionText>

        <SectionTitle variant="h2">6. Sus Derechos</SectionTitle>
        <SectionText>
          Dependiendo de su ubicación, puede tener los siguientes derechos respecto a su información personal:
        </SectionText>
        <SectionText component="ul" sx={{ pl: 4 }}>
          <li>Acceder a su información personal</li>
          <li>Rectificar información inexacta</li>
          <li>Solicitar la eliminación de su información</li>
          <li>Oponerse al procesamiento de su información</li>
          <li>Solicitar la portabilidad de sus datos</li>
          <li>Retirar su consentimiento en cualquier momento</li>
        </SectionText>
        <SectionText>
          Para ejercer estos derechos, puede contactarnos en: contact@peoplefortech.com
        </SectionText>

        <SectionTitle variant="h2">7. Retención de Datos</SectionTitle>
        <SectionText>
          Conservamos su información personal durante el tiempo necesario para cumplir con los propósitos 
          descritos en esta política, a menos que la ley requiera o permita un período de retención más largo.
        </SectionText>

        <SectionTitle variant="h2">8. Enlaces a Sitios de Terceros</SectionTitle>
        <SectionText>
          Nuestro sitio web puede contener enlaces a sitios web de terceros. No somos responsables de 
          las prácticas de privacidad de estos sitios externos. Le recomendamos revisar las políticas 
          de privacidad de cualquier sitio web que visite.
        </SectionText>

        <SectionTitle variant="h2">9. Menores de Edad</SectionTitle>
        <SectionText>
          Nuestros servicios no están dirigidos a menores de 18 años. No recopilamos intencionalmente 
          información personal de menores. Si descubrimos que hemos recopilado información de un menor, 
          tomaremos medidas para eliminar esa información.
        </SectionText>

        <SectionTitle variant="h2">10. Cambios a esta Política</SectionTitle>
        <SectionText>
          Podemos actualizar esta Política de Privacidad ocasionalmente. Le notificaremos sobre cambios 
          significativos publicando la nueva política en esta página y actualizando la fecha de "Última 
          actualización". Le recomendamos revisar esta política periódicamente.
        </SectionText>

        <SectionTitle variant="h2">11. Contacto</SectionTitle>
        <SectionText>
          Si tiene preguntas, inquietudes o solicitudes relacionadas con esta Política de Privacidad o 
          el tratamiento de su información personal, puede contactarnos en:
        </SectionText>
        <SectionText sx={{ fontWeight: 600, mt: 2 }}>
          Email: contact@peoplefortech.com
        </SectionText>
        <SectionText>
          PeopleForTech
        </SectionText>
      </PrivacyContainer>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;

